import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '@/utils/cn';

/**
 * Tooltip Component Props
 */
export interface TooltipProps {
  /** The element that triggers the tooltip (must accept ref) */
  children: React.ReactNode;
  /** The content to display in the tooltip */
  content: React.ReactNode;
  /** Which side of the trigger to show the tooltip */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** How to align the tooltip relative to the trigger */
  align?: 'start' | 'center' | 'end';
  /** Distance from the trigger in pixels */
  sideOffset?: number;
  /** Delay before showing tooltip in milliseconds */
  delayDuration?: number;
  /** Whether to skip showing the tooltip */
  disabled?: boolean;
  /** Controlled state for triggers that also open the tooltip on touch. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Additional classes for the tooltip content */
  className?: string;
}

/**
 * Tooltip Component
 *
 * A brand-styled tooltip using Radix UI primitives.
 * Displays contextual information on hover.
 *
 * NOTE: Requires TooltipProvider to be wrapped around the app.
 * Add to App.tsx:
 *   import { TooltipProvider } from '@/components/ui/Tooltip';
 *   <TooltipProvider><App /></TooltipProvider>
 *
 * @example
 * // Simple text tooltip
 * <Tooltip content="This is a tooltip">
 *   <button>Hover me</button>
 * </Tooltip>
 *
 * @example
 * // Rich content tooltip
 * <Tooltip
 *   content={
 *     <div>
 *       <p className="font-medium">Cannot publish</p>
 *       <p className="text-gray-300 text-xs">Add a caption or select channels first</p>
 *     </div>
 *   }
 * >
 *   <button disabled>Publish</button>
 * </Tooltip>
 *
 * @example
 * // Positioned tooltip
 * <Tooltip content="Info" side="right" align="start">
 *   <span>i</span>
 * </Tooltip>
 */
export function Tooltip({
  children,
  content,
  side = 'top',
  align = 'center',
  sideOffset = 6,
  delayDuration = 200,
  disabled = false,
  open,
  onOpenChange,
  className,
}: TooltipProps) {
  // Don't render tooltip wrapper if disabled or no content
  if (disabled || !content) {
    return <>{children}</>;
  }

  return (
    <TooltipPrimitive.Root delayDuration={delayDuration} open={open} onOpenChange={onOpenChange}>
      {/* Hover only: no tooltip on focus. A dialog that autofocuses a button (Cancel / Continue) would otherwise open
          that button's tooltip, which is then the topmost Radix layer and swallows the first Escape (two Esc to close
          the dialog). Radix runs this handler first and skips its own focus-open when it is default-prevented.
          Icon buttons carry aria-labels, so keyboard and screen reader users lose nothing. */}
      <TooltipPrimitive.Trigger asChild onFocus={(event) => event.preventDefault()}>
        {children}
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          className={cn(
            // Base styles - z-tooltip (120) ensures tooltip appears above everything
            'z-tooltip px-3 py-2 text-sm',
            // Brand styling - dark background, white text
            'bg-gray-900 text-white rounded-lg shadow-lg',
            // Max width for longer content
            'max-w-xs',
            // Animations
            'animate-fade-in',
            'data-[state=closed]:animate-fade-out',
            // Allow text to wrap for rich content
            'break-words',
            className
          )}
          side={side}
          align={align}
          sideOffset={sideOffset}
        >
          {content}
          <TooltipPrimitive.Arrow className="fill-gray-900" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

/**
 * TooltipProvider Component
 *
 * Must wrap your app (or the part using tooltips) to enable tooltip functionality.
 * Add this to your App.tsx:
 *
 * @example
 * import { TooltipProvider } from '@/components/ui/Tooltip';
 *
 * function App() {
 *   return (
 *     <TooltipProvider>
 *       <YourApp />
 *     </TooltipProvider>
 *   );
 * }
 *
 * @param delayDuration - Default delay for all tooltips (default: 200ms)
 * @param skipDelayDuration - Delay when moving between tooltips (default: 300ms)
 */
export const TooltipProvider = TooltipPrimitive.Provider;

/**
 * TooltipWrapper - For wrapping disabled elements
 *
 * Disabled buttons don't fire hover events on some browsers.
 * This wrapper ensures the tooltip works with disabled elements.
 *
 * @example
 * <TooltipWrapper content="This button is disabled because...">
 *   <button disabled>Submit</button>
 * </TooltipWrapper>
 */
export function TooltipWrapper({
  children,
  ...props
}: TooltipProps) {
  return (
    <Tooltip {...props}>
      <span className="inline-flex">{children}</span>
    </Tooltip>
  );
}

/**
 * Re-export primitives for advanced use cases
 */
export const TooltipRoot = TooltipPrimitive.Root;
export const TooltipTrigger = TooltipPrimitive.Trigger;
export const TooltipContent = TooltipPrimitive.Content;
export const TooltipArrow = TooltipPrimitive.Arrow;
export const TooltipPortal = TooltipPrimitive.Portal;
